import React, { useState, useEffect } from "react";
import { Form, Row, Col, Label, Input, FormFeedback, Button } from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";
import Select from "react-select";
import { toast } from "react-toastify";

import {
  calcSingleVolume,
  callErrorToast,
  callSuccessToast,
  orderBykey,
} from "../../utilities";
import ParcelTypesService from "../../services/shipment-service/parcelTypes";
import ParcelTemplatesService from "../../services/shipment-service/parcelTemplates";
import { useAppContext } from "../../AppProvider";

const parcelTypesService = new ParcelTypesService();
const parcelTemplatesService = new ParcelTemplatesService();

const Edit = ({ modalToogle, refreshTable, parcelTemplateId }) => {
  const [parcelTypes, setParcelTypes] = useState([]);

  const [parcelTypeCode, setParcelTypeCode] = useState({
    code: "PKG",
    name: "Pacco",
  });

  const { dictionary } = useAppContext();

  const getAllParcelTypes = () => {
    parcelTypesService
      .getAll()
      .then((response) => {
        setParcelTypes(orderBykey(response, "name"));
      })
      .catch((err) => {
        callErrorToast(err, dictionary);
      });
  };

  const getParcelTemplate = () => {
    parcelTemplatesService
      .getParcelTemplate(parcelTemplateId)
      .then((response) => {
        const parcelTypeCode = parcelTypes.find((elem) => {
          if (elem.code === response.parcelTypeCode) {
            return { ...elem };
          }
        });

        setParcelTypeCode(parcelTypeCode);
        validation.setValues(response);
      })
      .catch((err) => {
        callErrorToast(err, dictionary);
      });
  };

  const editParcelTemplate = () => {
    parcelTemplatesService
      .edit({ ...validation.values, parcelTypeCode: parcelTypeCode.code })
      .then((response) => {
        refreshTable();
        modalToogle();
        callSuccessToast(response, dictionary);
      })
      .catch((err) => {
        callErrorToast(err, dictionary);
      });
  };

  const handleSelectChange = (option, event) => {
    setParcelTypeCode(option);
  };

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      id: "",
      name: "",
      width: "",
      length: "",
      height: "",
      weight: "",
      volume: "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required(dictionary.messages.mandatory_fields),
      width: Yup.number().min(0),
      length: Yup.number().min(0),
      height: Yup.number().min(0),
      weight: Yup.number().min(0),
    }),
    onSubmit: (values) => {
      editParcelTemplate();
    },
  });

  useEffect(() => {
    const volume =
      calcSingleVolume(
        validation.values.width,
        validation.values.length,
        validation.values.height
      ) || "";
    validation.setFieldValue(
      "volume",
      volume.replace(/^0+(\d)|(\d)0+$/gm, "$1$2")
    );
  }, [
    validation.values.width,
    validation.values.length,
    validation.values.height,
  ]);

  useEffect(() => {
    getAllParcelTypes();
  }, []);

  useEffect(() => {
    getParcelTemplate();
  }, [parcelTypes]);

  return (
    <React.Fragment>
      <Form onSubmit={validation.handleSubmit}>
        <div className="row">
          <div className="col">
            <label className="form-label">{dictionary.words.typology}:</label>
            <Select
              name="parcelTypeCode"
              onChange={handleSelectChange}
              defaultMenuIsOpen={false}
              className="select"
              options={orderBykey(parcelTypes, "name")}
              value={
                parcelTypeCode || {
                  code: "PKG",
                  name: "Pacco",
                }
              }
              getOptionValue={(option) => option["code"]}
              getOptionLabel={(option) => option["name"]}
            />
          </div>
          <div className="col">
            <label className="form-label">{dictionary.registries.name}:</label>
            <Input
              type="text"
              name="name"
              className="form-control"
              placeholder="Nome template"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.name || ""}
              invalid={
                validation.touched.name && validation.errors.name ? true : false
              }
            />
            {validation.touched.name && validation.errors.name ? (
              <FormFeedback type="invalid">
                {validation.errors.name}
              </FormFeedback>
            ) : null}
          </div>
        </div>
        <div className="row mt-2">
          <div className="col">
            <label className="form-label">
              {dictionary.dimensions.width} (cm):
            </label>
            <Input
              type="number"
              name="width"
              className="form-control"
              placeholder="100"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.width || ""}
              invalid={
                validation.touched.width && validation.errors.width
                  ? true
                  : false
              }
            />
            {validation.touched.width && validation.errors.width ? (
              <FormFeedback type="invalid">
                {validation.errors.width}
              </FormFeedback>
            ) : null}
          </div>
          <div className="col">
            <label className="form-label">
              {dictionary.dimensions.length} (cm):
            </label>
            <Input
              type="number"
              name="length"
              className="form-control"
              placeholder="100"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.length || ""}
              invalid={
                validation.touched.length && validation.errors.length
                  ? true
                  : false
              }
            />
            {validation.touched.length && validation.errors.length ? (
              <FormFeedback type="invalid">
                {validation.errors.length}
              </FormFeedback>
            ) : null}
          </div>
          <div className="col">
            <label className="form-label">
              {dictionary.dimensions.height} (cm):
            </label>
            <Input
              type="number"
              name="height"
              className="form-control"
              placeholder="100"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.height || ""}
              invalid={
                validation.touched.height && validation.errors.height
                  ? true
                  : false
              }
            />
            {validation.touched.height && validation.errors.height ? (
              <FormFeedback type="invalid">
                {validation.errors.height}
              </FormFeedback>
            ) : null}
          </div>
        </div>
        <div className="row mt-2">
          <div className="col-4">
            <label className="form-label">
              {dictionary.dimensions.weight} (kg):
            </label>
            <Input
              type="number"
              name="weight"
              className="form-control"
              placeholder="1"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.weight || ""}
              invalid={
                validation.touched.weight && validation.errors.weight
                  ? true
                  : false
              }
            />
            {validation.touched.weight && validation.errors.weight ? (
              <FormFeedback type="invalid">
                {validation.errors.weight}
              </FormFeedback>
            ) : null}
          </div>
          <div className="col-4">
            <label className="form-label">
              {dictionary.dimensions.volume} (m3):
            </label>
            <Input
              type="number"
              name="volume"
              disabled={true}
              className="form-control"
              placeholder="100"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.volume || ""}
              invalid={
                validation.touched.volume && validation.errors.volume
                  ? true
                  : false
              }
            />
            {validation.touched.volume && validation.errors.volume ? (
              <FormFeedback type="invalid">
                {validation.errors.volume}
              </FormFeedback>
            ) : null}
          </div>
        </div>
        <div className="d-flex justify-content-end mt-3">
          <button type="submit" className="btn btn-success ms-3">
            {dictionary.actions.save}{" "}
            <i className="ph-paper-plane-tilt ms-2"></i>
          </button>
        </div>
      </Form>
    </React.Fragment>
  );
};

export default Edit;
